import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { DECKS } from '../data/stimuli';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/sequencing-generator.css';

const MIN_BINS = 2;
const MAX_BINS = Math.min(20, DECKS.length);

// ══════════════════════════════════════════════════════════════════════════════
// Categories setup — nothing is pre-made or saved. Every time someone plays,
// they choose how many categories to sort between and which ones, then jump
// straight into the sorting game with those decks.
// ══════════════════════════════════════════════════════════════════════════════

export const CategoriesSetupPage = () => {
  const navigate = useNavigate();
  const { t, tc } = useTranslation();

  const [binCount, setBinCount] = useState(MIN_BINS);
  const [selectedDecks, setSelectedDecks] = useState([]);

  const toggleDeck = (deck) => {
    setSelectedDecks(prev => {
      const already = prev.some(d => d.name === deck.name);
      if (already) return prev.filter(d => d.name !== deck.name);
      if (prev.length >= binCount) return prev;
      return [...prev, deck];
    });
  };

  const canStart = selectedDecks.length === binCount
    && selectedDecks.every(d => d.items.filter(c => c.image).length >= 2);

  const handleStart = () => {
    if (!canStart) return;
    navigate('/categories/play', { state: { deckNames: selectedDecks.map(d => d.name) } });
  };

  const changeBinCount = (n) => {
    const clamped = Math.max(MIN_BINS, Math.min(MAX_BINS, n));
    setBinCount(clamped);
    setSelectedDecks(prev => prev.slice(0, clamped));
  };

  return (
    <Layout>
      <div className="gen-page">

        <div className="gen-header">
          <div className="gen-header-icon">🗂️</div>
          <div>
            <h1 className="gen-title">{t('catLib.title')}</h1>
            <p className="gen-subtitle">{t('catLib.subtitle')}</p>
          </div>
        </div>

        <div className="gen-input-card">

          {/* ── Step 1: how many ── */}
          <label className="gen-label">{t('sort.howMany')}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 6 }}>
            <button
              onClick={() => changeBinCount(binCount - 1)}
              disabled={binCount <= MIN_BINS}
              aria-label="Fewer categories"
              style={{
                width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #E5E7EB',
                background: '#F9FAFB', fontSize: 20, fontWeight: 700, color: '#374151',
                cursor: binCount <= MIN_BINS ? 'not-allowed' : 'pointer',
                opacity: binCount <= MIN_BINS ? 0.4 : 1,
              }}
            >−</button>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#111827', minWidth: 32, textAlign: 'center' }}>
              {binCount}
            </span>
            <button
              onClick={() => changeBinCount(binCount + 1)}
              disabled={binCount >= MAX_BINS}
              aria-label="More categories"
              style={{
                width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #E5E7EB',
                background: '#F9FAFB', fontSize: 20, fontWeight: 700, color: '#374151',
                cursor: binCount >= MAX_BINS ? 'not-allowed' : 'pointer',
                opacity: binCount >= MAX_BINS ? 0.4 : 1,
              }}
            >+</button>
          </div>
          <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 28px' }}>
            {t('sort.howManyHelp')}
          </p>

          {/* ── Step 2: which ones ── */}
          <label className="gen-label">
            {t('sort.whichCategories')} ({selectedDecks.length}/{binCount})
          </label>

          {selectedDecks.length > 0 && (
            <div className="gen-chips" style={{ marginTop: 0, marginBottom: 16 }}>
              {selectedDecks.map(deck => (
                <div
                  key={deck.name}
                  className="gen-chip"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, cursor: 'default',
                    borderColor: '#8B2635', color: '#8B2635', background: '#FFF5F5',
                  }}
                >
                  <img
                    src={deck.coverImage} alt=""
                    style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }}
                  />
                  <span>{tc(deck.name)}</span>
                  <button
                    onClick={() => toggleDeck(deck)}
                    title={`Remove ${deck.name}`}
                    style={{
                      width: 16, height: 16, borderRadius: '50%', border: 'none', background: '#8B2635',
                      color: '#fff', fontSize: 9, fontWeight: 800, cursor: 'pointer', lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                    }}
                  >✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 8 }}>
            {DECKS.map(deck => {
              const count = deck.items.filter(c => c.image).length;
              const selected = selectedDecks.some(d => d.name === deck.name);
              const disabled = !selected && selectedDecks.length >= binCount;
              return (
                <button
                  key={deck.name}
                  onClick={() => toggleDeck(deck)}
                  disabled={disabled}
                  className={`gen-diff-btn ${selected ? 'gen-diff-btn--on' : ''}`}
                  style={{ '--diff-color': '#8B2635', opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  <img
                    src={deck.coverImage} alt=""
                    style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover' }}
                  />
                  <span className="gen-diff-label">{tc(deck.name)}</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>{count} cards</span>
                </button>
              );
            })}
          </div>
          <button className="gen-cta" onClick={handleStart} disabled={!canStart}>
            {t('sort.startSorting')}
          </button>
        </div>
      </div>
    </Layout>
  );
};
